import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Save, Send, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Toggle from "@/components/app/Toggle";
import { Field, isBlank, isEmail } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, cardCls, fmtDateTime } from "@/components/app/ui";

const SOURCE_TEXT = {
  database: "Using the API key saved here.",
  environment: "Using the key from the server environment (EMAIL_API_KEY).",
  none: "No API key is set, so no emails are being sent.",
};

export default function EmailSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [keyErr, setKeyErr] = useState("");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings/email");
    setCfg(data); setEnabled(data.key_set ? data.enabled : true);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load email settings.")); }, [load]);

  const save = async () => {
    const k = apiKey.trim();
    if (!k && !cfg.key_set) { setKeyErr("Enter the email API key."); return; }
    if (k && (k.length < 8 || /\s/.test(k))) { setKeyErr("That API key doesn't look right."); return; }
    setKeyErr(""); setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/email", { api_key: k || null, enabled });
      setCfg(data); setApiKey("");
      toast.success(data.enabled ? "Email API key saved." : "Saved, but email sending is switched off.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the saved email API key? Emails stop unless the server has an EMAIL_API_KEY.")) return;
    try { const { data } = await api.delete("/admin/settings/email"); setCfg(data); setEnabled(true); toast.success("Saved key removed."); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (isBlank(testTo) || !isEmail(testTo)) { toast.error("Enter a valid email address for the test."); return; }
    setTesting(true);
    try { const { data } = await api.post("/admin/settings/email/test", { to: testTo.trim() }); toast.success(data.message); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const none = cfg.active_source === "none";
  return (
    <section className={`${cardCls} p-5`} data-testid="email-settings">
      <div className="flex items-center gap-2 mb-1"><Mail size={16} className="text-blue-600" /><h2 className="text-[14px] font-bold">Email service</h2>
        {cfg.key_set && <span className="ml-auto flex items-center gap-2 text-[12px] text-slate-600">{enabled ? "Enabled" : "Disabled"}<Toggle checked={enabled} onChange={setEnabled} label="Send emails" t="email-enabled" /></span>}</div>
      <p className="text-[12px] text-slate-500 mb-3">API key of the email service used for every platform email — signup welcome, new-order alerts to vendors and customers, order status updates and payment receipts.</p>
      <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 mb-3 text-[12px] ${none ? "bg-orange-50 border-orange-200 text-orange-800" : "bg-green-50 border-green-200 text-green-800"}`} data-testid="email-status">
        {none ? <AlertTriangle size={15} className="mt-0.5" /> : <CheckCircle2 size={15} className="mt-0.5" />}<span>{SOURCE_TEXT[cfg.active_source]}</span>
      </div>
      <Field label="API key" type="password" t="email-api-key" autoComplete="new-password" value={apiKey} onChange={setApiKey} error={keyErr}
        placeholder={cfg.key_set ? `${cfg.key_hint} (saved — leave blank to keep)` : "Paste the API key"} hint="Stored encrypted. It is never shown again after saving. Change it here whenever the email service changes." />
      <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
        <div className="text-[11px] text-slate-500">{cfg.updated_at ? `Last updated ${fmtDateTime(cfg.updated_at)}` : "No key saved yet."}</div>
        <div className="flex gap-2">
          {cfg.key_set && <button onClick={remove} data-testid="email-remove" className={btnOutline}><Trash2 size={14} /> Remove key</button>}
          <button onClick={save} disabled={saving} data-testid="email-save" className={btnPrimary}><Save size={14} /> {saving ? "Saving…" : "Save key"}</button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-end gap-2 flex-wrap">
        <Field className="flex-1 min-w-[220px]" label="Send a test email to" type="email" t="email-test-to" value={testTo} onChange={setTestTo} placeholder="you@company.com" />
        <button onClick={sendTest} disabled={testing || none} data-testid="email-test" className={btnOutline}><Send size={14} /> {testing ? "Sending…" : "Send test"}</button>
      </div>
    </section>
  );
}
